import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import type { Page, PriorityOption } from '../../common/ui/types';
import type { PriorityOnServerPageResponse, RefinedFetchError } from '../types';
import { refineFetchError, refinePriorityOnServerPageResponse } from '../utils';

export const PRIORITY_API_URL = '/rest/api/3/priority';

export const toPriorityOption = (p: unknown, sequence: number): PriorityOption => {
	if (
		!p ||
		typeof p !== 'object' ||
		!('id' in p) ||
		typeof p.id !== 'string' ||
		!('name' in p) ||
		typeof p.name !== 'string' ||
		!('iconUrl' in p) ||
		typeof p.iconUrl !== 'string'
	) {
		throw new Error('Unexpected priority object');
	}

	return {
		id: p.id,
		name: p.name,
		iconUrl: p.iconUrl,
		sequence,
	};
};

export const fetchAvailablePriorities = async (
	query: string,
	page: number,
	pageSize: number,
	exclude: string[],
	selectedPriorities: string[] = [],
): Promise<Page<PriorityOption>> => {
	const response: unknown = await fetchJson(PRIORITY_API_URL);

	if (!response || !Array.isArray(response)) {
		throw new Error('Unexpected response from /rest/api/3/priority');
	}

	const allPriorities = response.map(toPriorityOption);
	const excludedPriorityIds: Record<string, true> = {};
	exclude.forEach((id) => {
		excludedPriorityIds[id] = true;
	});
	const matchingPriorities = allPriorities.filter(
		(p) => !excludedPriorityIds[p.id] && p.name.toLowerCase().includes(query.toLowerCase()),
	);

	return {
		items: matchingPriorities.slice(
			page * pageSize - selectedPriorities.length,
			(page + 1) * pageSize,
		),
		currentPage: page,
		pageSize,
		totalItems: matchingPriorities.length,
	};
};

export const fetchPrioritiesByScheme = async (
	schemeId: string,
	startAt: number,
	maxResults: number,
): Promise<PriorityOnServerPageResponse> => {
	try {
		const URL = `/rest/api/2/priorityscheme/${schemeId}/priorities?startAt=${startAt}&maxResults=${maxResults}`;
		const response: unknown = await fetchJson(URL);

		const responseBody = refinePriorityOnServerPageResponse(response);

		if (!responseBody) {
			throw new Error('Invalid response');
		}

		return responseBody;
	} catch (error) {
		// We don't care if the error is a 400 or 403 we will deal with them in
		// in the same way i imagine.
		const fetchError: RefinedFetchError | undefined = await refineFetchError(error);
		fireErrorAnalytics({
			error: fetchError
				? new Error(
						`get priority by scheme failed with status code ${fetchError.statusCode} and response body ${fetchError.responseJson}`,
					)
				: new Error('get priority by scheme failed with unexpected error'),
			meta: {
				id: 'getPriorityByScheme',
				packageName: 'jiraAdminPagesPrioritySchemes',
				teamName: 'morphinecake',
			},
			sendToPrivacyUnsafeSplunk: true,
		});

		throw error;
	}
};
